import React from "react";
import { cn } from "@/lib/utils";

type PageLayoutVariant = "centered" | "scroll";

interface PageLayoutProps {
  children: React.ReactNode;
  variant?: PageLayoutVariant;
  /** Max width token for inner content */
  maxWidth?: "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl" | "5xl" | "6xl" | "7xl" | "full";
  className?: string;
  innerClassName?: string;
}

const maxWidthClass: Record<NonNullable<PageLayoutProps["maxWidth"]>, string> = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-xl",
  "2xl": "max-w-2xl",
  "3xl": "max-w-3xl",
  "4xl": "max-w-4xl",
  "5xl": "max-w-5xl",
  "6xl": "max-w-6xl",
  "7xl": "max-w-7xl",
  full: "max-w-full",
};

/**
 * Single scroll container lives in AppShell <main>.
 * Pages must not use min-h-screen (avoids double scroll / extra gaps).
 */
export function PageLayout({
  children,
  variant = "scroll",
  maxWidth = "6xl",
  className,
  innerClassName,
}: PageLayoutProps) {
  const inner = (
    <div
      className={cn(
        "mx-auto w-full",
        maxWidthClass[maxWidth],
        innerClassName
      )}
    >
      {children}
    </div>
  );

  if (variant === "centered") {
    return (
      <div
        className={cn(
          "flex min-h-0 flex-1 items-center justify-center px-4 py-4 sm:px-6 sm:py-6",
          className
        )}
      >
        {inner}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "min-h-0 flex-1 px-4 py-4 sm:px-6 sm:py-6",
        className
      )}
    >
      {inner}
    </div>
  );
}

export function PageLoadingState() {
  return (
    <div className="flex min-h-0 flex-1 items-center justify-center bg-black">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-white/30 border-t-white" />
    </div>
  );
}
